/**
 * Auto-tuner evaluation core — store/DOM-free so it can run on the main thread
 * OR inside a Web Worker (see `tuner-worker.ts` / `tuner-pool.ts`).
 *
 * This module deliberately imports ONLY the engine/constraint layer (no
 * `./config-store`, no `document`), which is what makes it worker-safe: a
 * Web Worker has no DOM and cannot reach the web store. The "frozen world"
 * (`FrozenEnv`) is captured once on the main thread by `freezeEnv` (in
 * `auto-tuner.ts`) and handed in here as plain, structured-clone-safe data.
 */

import { computeScheduleScore } from '../constraints/soft-constraints';
import { getEffectiveConfig } from '../engine/effective-config';
import { SchedulingEngine } from '../engine/scheduler';
import {
  DEFAULT_CONFIG,
  type Participant,
  type ParticipantCapacity,
  type Schedule,
  type SchedulerConfig,
  type Task,
} from '../models/types';

export interface EvalResult {
  /**
   * Weight-independent score: schedule re-scored under `DEFAULT_CONFIG` so
   * candidates with different weights are on the same scale. This is the
   * only score used for ranking across candidates.
   */
  refScore: number;
  unfilled: number;
  feasible: boolean;
  wallMs: number;
}

/** Engine inputs captured once per tuning run so all evaluations see the same world. */
export interface FrozenEnv {
  dayStart: number;
  disabledHC: Set<string>;
  /** Rest-rule id → recovery window in ms. */
  restRuleMap: Map<string, number>;
  /**
   * Live shift-splitting mode, frozen once per run so every evaluation
   * exercises the SAME solver the user actually runs. Stamped onto the engine
   * via the 5th constructor arg below.
   */
  splittingMode: 'off' | 'feasibility' | 'quality';
  /** notWithPairs required for the reference-score SC-9 term. */
  notWithPairs: Map<string, Set<string>>;
  taskMap: Map<string, Task>;
  pMap: Map<string, Participant>;
  /**
   * Per-participant capacities over the run's task span. Required so the
   * reference re-score uses capacity-PROPORTIONAL SC-3/SC-8 targets — the same
   * objective production scores with. Without it the yardstick degrades to
   * flat-mean targets and mis-ranks the fairness/daily weights on
   * uneven-availability datasets.
   */
  capacities: Map<string, ParticipantCapacity>;
}

export interface EvalParams {
  participants: Participant[];
  tasks: Task[];
  config: SchedulerConfig;
  attempts: number;
  maxIterations: number;
  frozen: FrozenEnv;
  /** Cancels the in-flight engine run when the user cancels the tune. */
  abortSignal?: AbortSignal;
  /**
   * Per-eval SA wall-clock cap. The tuner computes this on the main thread (the
   * legacy `solverRatio`-scaled value, see `auto-tuner.mkJob`) and passes it down
   * so each evaluation's budget is byte-identical to the pre-pool serial tuner.
   * Falls back to the candidate config's own cap if a caller omits it.
   */
  maxSolverTimeMs?: number;
}

/**
 * Run a full multi-attempt scheduling with the candidate weights and re-score
 * the result under the fixed reference config. Pure w.r.t. the web layer —
 * reads nothing global, builds a fresh engine, returns a plain result.
 */
export async function runEvaluation(params: EvalParams): Promise<EvalResult> {
  const start = performance.now();
  const { dayStart, disabledHC, restRuleMap, notWithPairs, splittingMode } = params.frozen;

  // Per-phase fidelity comes from `maxIterations`; the wall-clock cap is the
  // legacy `solverRatio`-scaled value computed by the caller (auto-tuner.mkJob),
  // so each eval is byte-identical to the pre-pool serial tuner. Falls back to
  // the candidate config's own cap if omitted.
  const scaledConfig: SchedulerConfig = {
    ...params.config,
    maxIterations: params.maxIterations,
    maxSolverTimeMs: params.maxSolverTimeMs ?? params.config.maxSolverTimeMs,
  };

  const engine = new SchedulingEngine(scaledConfig, disabledHC, restRuleMap, dayStart, splittingMode);
  engine.addParticipants(params.participants);
  engine.addTasks(params.tasks);

  let schedule: Schedule;
  try {
    schedule = await engine.generateScheduleAsync(params.attempts, undefined, params.abortSignal);
  } catch (_err) {
    // A scheduling failure during tuning is treated as a maximally-infeasible
    // evaluation: strongly discouraged, but doesn't abort the tournament.
    return { refScore: -1e18, unfilled: 9999, feasible: false, wallMs: performance.now() - start };
  }

  const unfilled = schedule.violations.filter((v) => v.code === 'INFEASIBLE_SLOT').length;

  // Re-score the produced schedule under a fixed reference config so results
  // from different candidate weight vectors are directly comparable. Score
  // against the REALIZED task set (`schedule.tasks`, post-split) with a taskMap
  // rebuilt from it — the frozen whole-task map omits the `#a`/`#b` split-half
  // ids, which would silently drop split-half assignments from every soft term.
  // Capacities stay frozen (the window is split-invariant). When nothing split,
  // `schedule.tasks === params.tasks` by reference ⇒ byte-identical. The
  // reference config routes through getEffectiveConfig so feasibility-mode
  // splits stay free in the yardstick (quality mode is returned unchanged).
  const realizedTasks = schedule.tasks ?? params.tasks;
  const refConfig = getEffectiveConfig(DEFAULT_CONFIG, splittingMode);
  const refScoreObj = computeScheduleScore(realizedTasks, params.participants, schedule.assignments, refConfig, {
    taskMap: realizedTasks === params.tasks ? params.frozen.taskMap : new Map(realizedTasks.map((t) => [t.id, t])),
    pMap: params.frozen.pMap,
    capacities: params.frozen.capacities,
    notWithPairs,
    dayStartHour: dayStart,
  });

  return {
    refScore: refScoreObj.compositeScore,
    unfilled,
    feasible: unfilled === 0,
    wallMs: performance.now() - start,
  };
}
